// src/data/policies.js
//
// Policy copy. Numbers come from ./practice (owner-confirmable, mirrored by
// the backend config); crisis wording comes from CrisisNotice / ./crisis.
// Headings start at h3: Policies.jsx renders the section label as the h2.
import React from "react";
import { Link } from "react-router-dom";
import config from "../config";
import CrisisNotice from "../components/CrisisNotice";
import { BUNDLE_CANCELLATION_TEXT } from "./offerings";
import {
  PRACTICE,
  POLICY_LAST_UPDATED,
  formatPolicyDate,
  lateCancelFeeText,
  paymentTimingText,
  BOOKING_REQUEST_TEXT,
} from "./practice";

/**
 * Version of the published policy wording. Changes whenever the owner
 * approves new copy (same value as the "Last updated" date).
 */
export const POLICY_VERSION = POLICY_LAST_UPDATED;

const contactEmail = config.business.contactEmail;
const paymentsEnabled = config.features.paymentsEnabled;
const {
  cancellationWindowHours: CANCEL_HOURS,
  minBookingNoticeHours: NOTICE_HOURS,
  groupWithdrawalHours: GROUP_HOURS,
  meetingPlatform,
  minClientAge,
} = PRACTICE;

const Email = () => <a href={`mailto:${contactEmail}`}>{contactEmail}</a>;

/** Where the Social Media and Communication Policy lives (its own tab). */
export const SOCIAL_MEDIA_POLICY_PATH = "/policies#social-media";

const SocialMediaPolicyLink = () => (
  <Link to={SOCIAL_MEDIA_POLICY_PATH}>Social Media and Communication Policy</Link>
);

const LastUpdated = () => (
  <p><em>Last updated: {formatPolicyDate()}</em></p>
);

export const POLICY_SECTIONS = [

  {
    key: "refund",
    label: "Refund & Cancellation Policy",
    content: (
      <>
        <LastUpdated />
      <h3><strong>Legal Disclaimer</strong></h3>
        <p>
          This Refund Policy is provided for informational purposes only and does not constitute a binding legal contract. Healing Hues reserves the right to update or modify this policy at any time. Any changes will be communicated clearly and posted on this page.
        </p>
        <p>
          Healing Hues is a provider of online art therapy and counseling services. By booking a session or group, clients acknowledge and agree to the terms outlined below. This policy is intended to protect both client and clinician and to ensure clarity, fairness, and respect for time and resources.
        </p>
        <h3><strong>Payment</strong></h3>
        <ul>
          <li>{paymentTimingText()} To receive bank transfer details, email <Email />.</li>
          <li>Online bookings must be made at least <strong>{NOTICE_HOURS} hours</strong> before the session starts.</li>
          <li>
            Nothing is charged or refunded automatically. Any late-cancellation or no-show fee is
            added to the amount due at your next session, or invoiced by email.
          </li>
        </ul>

        <h3><strong>Refund Policy</strong></h3>
        <p>
          At Healing Hues, your care, comfort, and clarity matter deeply. This refund policy
          outlines what you can expect when it comes to payments, cancellations, and rescheduling.
        </p>

        <h4>Individual Sessions</h4>
        <ul>
          <li>Fees paid for a session that has taken place are non-refundable.</li>
          <li>
            If you need to reschedule, please do so at least <strong>{CANCEL_HOURS} hours</strong> before your
            scheduled appointment to avoid a late-cancellation fee. Rescheduling is subject to therapist availability.
          </li>
        </ul>

        <h4>Therapy Bundles</h4>
        <ul>
          <li>You may discontinue any service, including a bundle, at any point.</li>
          <li>{BUNDLE_CANCELLATION_TEXT}</li>
        </ul>

        <h4>Group Art Therapy Programs</h4>
        <ul>
          <li>
            Fees paid for group sessions (such as a six-week art therapy group) are
            <strong> non-refundable</strong> once the group has begun.
          </li>
          <li>
            If you withdraw <strong>{GROUP_HOURS} hours or more</strong> before the first session, you may request a full refund of any fee you have paid.
          </li>
          <li>No refunds are issued for missed group sessions.</li>
        </ul>

        <h4>Sliding Scale &amp; Accessibility</h4>
        <p>
          Healing Hues offers sliding scale options to support accessibility and equity. Because of this,
          refunds for discounted sessions are not available, unless in exceptional circumstances.
          If your financial situation changes, please reach out—I’m happy to explore adjustments
          that honor both your needs and the value of care being provided.
        </p>

        <h3><strong>Cancellation Policy</strong></h3>
        <p>
          I understand that life can be unpredictable. If you need to cancel or reschedule your session,
          please provide at least <strong>{CANCEL_HOURS} hours' notice</strong>. This allows me to offer that time to
          someone else seeking support.
        </p>
        <p>
          Cancellations made less than {CANCEL_HOURS} hours in advance or missed sessions (no-shows) may incur a fee of up to{" "}
          {lateCancelFeeText()}, unless due to an emergency. Each client is granted a <strong>one-time no-show
          waiver</strong> for a missed session with no notice. After that, additional no-shows may result in fees
          or a pause in services. I appreciate your understanding and commitment to this policy—it supports
          fairness and consistency for all clients.
        </p>

        <h4>Emergencies or Exceptional Situations</h4>
        <p>
          If an emergency or unforeseen circumstance prevents you from attending your session or program,
          please contact me as soon as possible. Late-cancellation fees may be waived, and partial refunds or
          session credits for fees already paid may be considered, on a case-by-case basis.
        </p>

        <h4>Therapist Cancellations</h4>
        <ul>
          <li>Clients will be notified as soon as possible via email or phone.</li>
          <li>Any fees paid for the canceled session will be fully refunded or credited toward a future session, based on your preference.</li>
          <li>I will do my best to offer a prompt rescheduling option that works with your availability.</li>
        </ul>

        <h4>Questions?</h4>
        <p>
          Email me at <Email /> if you have questions
          or need clarity around your payment or refund eligibility.
        </p>
      </>
    ),
  },

  {
    key: "privacy",
    label: "Privacy Policy",
    content: (
      <>
        <LastUpdated />
      <h3><strong>Legal Disclaimer</strong></h3>
        <p>
          Healing Hues provides online art therapy and clinical mental health counseling services for individuals located in The Bahamas. All services are intended for informational and therapeutic purposes only and do not constitute legal, medical, or emergency care. Participation in services does not create a doctor-patient or emergency care relationship.</p>
        <CrisisNotice />
        <p>
          The content provided on this website is not a substitute for professional advice or diagnosis. While every effort is made to protect your confidentiality, internet-based services carry certain inherent privacy risks. By using this site and engaging in services, you acknowledge and accept these terms.
          </p>

      <h3><strong>Privacy Policy</strong></h3>
        <h4><strong>Information Collection</strong></h4>
        <p>When you use this website, Healing Hues may collect personal information such as your:</p>
        <ul>
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number (optional)</li>
          <li>The service, date and time you book, and any note you add</li>
          <li>Messages you send through the contact form</li>
          <li>Relevant health information (only when you choose to share it with your therapist for your care)</li>
        </ul>
        <p>
          Information may be collected via the booking and contact forms or email communication. The online booking
          form does not ask for health information; please don't include sensitive details in booking notes or
          contact messages, and share them in session instead.
        </p>

        <h4><strong>Use of Information</strong></h4>
        <ul>
          <li>Scheduling and coordinating therapy services</li>
          <li>Communicating with you regarding appointments and updates</li>
          <li>Maintaining clinical records in accordance with professional and legal standards</li>
          <li>Improving service delivery and accessibility</li>
        </ul>
        <p>Your information will <strong>never</strong> be sold or shared with third parties for marketing purposes.</p>

        <h4><strong>Service Providers</strong></h4>
        <p>
          I use a small number of service providers to run bookings and online sessions. They handle your
          information only to provide their service to me:
        </p>
        <ul>
          <li>
            <strong>Google Calendar</strong> (Google): your appointment, including your name, email, phone number,
            service, time and any booking note, is saved in the therapist's calendar. Google does not send you a
            calendar invitation; your confirmation comes from me by email.
          </li>
          <li>
            <strong>{meetingPlatform}</strong>: hosts your video sessions. Each session uses a private link sent to
            you by email.
          </li>
          <li>
            <strong>Email delivery provider</strong>: sends booking confirmations, calendar files, cancellation
            notices and replies to contact messages, so it handles your name, email address and appointment details.
          </li>
          <li>
            <strong>Website hosting provider</strong>: runs this website and the booking system and stores its
            database, including technical logs such as IP addresses.
          </li>
          <li>
            <strong>Sentry</strong> (error monitoring): receives technical reports when something on the website
            goes wrong. I configure it to remove email addresses from these reports.
          </li>
          {paymentsEnabled && (
            <li>
              <strong>Plug n Pay</strong> (payment processing): processes card payments when you pay online. Card
              details go directly to the payment processor; Healing Hues does not store your card number.
            </li>
          )}
        </ul>

        <h4><strong>Confidentiality &amp; Limits</strong></h4>
        <p>All clinical services follow the ethical guidelines set by:</p>
        <ul>
          <li>American Art Therapy Association (AATA)</li>
          <li>American Counseling Association (ACA)</li>
          <li>National Board for Certified Counselors (NBCC/NCC)</li>
          <li>The Bahamas Mental Health Act</li>
        </ul>
        <p>Client information remains confidential except in the following cases:</p>
        <ul>
          <li>You are at risk of harming yourself or others</li>
          <li>There is suspected abuse or neglect of a minor, elder, or vulnerable person</li>
          <li>Records are subpoenaed by a court of law</li>
          <li>You provide written consent to share information</li>
        </ul>

        <h4><strong>Shared Confidentiality</strong></h4>
        <p>
          Confidentiality is a shared effort. I hold sessions from a private space, and I ask you to join from a
          private room where you won't be overheard, whether your session is online or in your home.
        </p>

        <h4><strong>Social Media &amp; Communication</strong></h4>
        <p>
          Healing Hues maintains a separate <SocialMediaPolicyLink />. Clients are encouraged to review it before
          engaging with Healing Hues on social media platforms. The policy outlines expectations around boundaries,
          communication, and privacy in digital spaces.
        </p>

        <h4><strong>Storage &amp; Security</strong></h4>
        <p>
          Client records are stored digitally in encrypted and password-protected formats. Healing Hues uses secure
          methods of communication and documentation to protect your personal information. However, no method of
          transmission over the internet is 100% secure. By participating in online therapy, you acknowledge these limitations.
        </p>

        <h4><strong>How Long I Keep Information</strong></h4>
        <ul>
          <li>
            Clinical records are kept for the period required by professional record-keeping standards and the laws
            of The Bahamas, then securely deleted.
          </li>
          <li>
            Booking details and contact-form messages that do not lead to ongoing services are kept only as long as
            needed to respond to you and manage appointments, then deleted.
          </li>
          <li>You can ask me to delete your information at any time, within the limits of those legal requirements.</li>
        </ul>

        <h4><strong>Telehealth Considerations</strong></h4>
        <p>Sessions are held via secure telehealth platforms. Clients are encouraged to:</p>
        <ul>
          <li>Use a private, quiet space for sessions</li>
          <li>Avoid public or shared devices when communicating</li>
          <li>Use strong passwords and secure internet connections</li>
        </ul>
        <p>Your confidentiality is a shared responsibility—please do your part to protect your space during sessions.</p>

        <h4><strong>Your Rights &amp; Contact</strong></h4>
        <p>You have the right to:</p>
        <ul>
          <li>Request a copy of your records</li>
          <li>Ask how your data is being used</li>
          <li>Request corrections or withdrawal of consent (within legal limits)</li>
        </ul>
        <p>
          For questions, concerns, or to make a request regarding your privacy:{" "}
          <Email />
        </p>
      </>
    ),
  },

  {
    key: "terms",
    label: "Terms & Conditions",
    content: (
      <>
        <LastUpdated />
      <h3><strong>Legal Disclaimer</strong></h3>
        <p>
          The information provided on this website is intended for general educational and informational purposes only. It does not constitute mental health or medical advice, diagnosis, or treatment, and should not be relied upon as such.
          </p>
        <p>
          Engaging with content on this website or submitting a request through it does not establish a therapist-client relationship until a formal agreement is completed.
          </p>
          <CrisisNotice />
      <h3><strong>Terms &amp; Conditions</strong></h3>
        <p>
          Welcome to Healing Hues. By accessing or using this website and engaging with services provided,
          you agree to the following Terms and Conditions. Please read them carefully before booking or using this site.
        </p>

        <h4><strong>Nature of Services</strong></h4>
        <p>
          Healing Hues offers art therapy and clinical mental health counseling services. I work with children, teens
          and adults. Online booking on this website is for adults ({minClientAge}+); parents and guardians can email
          me at <Email /> to arrange sessions for children and teens.
        </p>
        <p>
          All services are intended for personal growth, emotional processing, and support—<em>not</em> emergency mental health care.
          If you are in crisis or need immediate help, follow the emergency guidance above.
        </p>

        <h4><strong>Eligibility</strong></h4>
        <p>
          Services are available to residents of The Bahamas. At this time, Healing Hues does not offer services to residents
          of Kansas or other U.S. states.
        </p>

        <h4><strong>Booking and Payment</strong></h4>
        <p>
          Therapy appointments are booked through this website's booking page (at least{" "}
          {NOTICE_HOURS} hours in advance) or by email. {BOOKING_REQUEST_TEXT} A sliding scale fee structure is available—details are provided
          in the client intake documents. {paymentTimingText()} Any other payment arrangements are set out in your
          service agreement.
        </p>

        <h4><strong>Cancellations and No-Shows</strong></h4>
        <p>
          Please cancel or reschedule at least <strong>{CANCEL_HOURS} hours</strong> in advance of your session. Missed appointments
          or late cancellations may incur a fee of up to {lateCancelFeeText()}. Full details are in
          my <Link to="/policies#refund">Refund &amp; Cancellation Policy</Link> and the service agreement provided upon intake.
        </p>

        <h4><strong>Confidentiality and Privacy</strong></h4>
        <p>
          All therapy sessions are confidential, in accordance with professional ethical standards and the Bahamas Mental Health Act,
          the AATA Code of Ethics, the ACA Code of Ethics, and the National Certified Counselor (NCC) Code of Ethics.
          Please review the full <Link to="/policies#privacy">Privacy Policy</Link> for details on how your information is collected, used, and protected.
        </p>

        <h4><strong>Photography and Social Media Consent</strong></h4>
        <p>
          No images, artwork, or identifying information relating to clients will be shared publicly without explicit
          consent. Clients may withdraw consent at any time by contacting <Email />. Healing Hues maintains a
          separate <SocialMediaPolicyLink /> which outlines expectations around digital communication and boundaries.
        </p>

        <h4><strong>Website Use</strong></h4>
        <p>
          All content on this site—including text, images, blog posts, and downloadable resources—is the property of Healing Hues.
          You may not reproduce, republish, or use any content for commercial purposes without permission. Use of the website is
          for informational purposes only and does not constitute a therapeutic relationship until a formal agreement is established.
        </p>

        <h4><strong>Limitation of Liability</strong></h4>
        <p>
          Healing Hues and its founder, Jasmine G. Smith, are not liable for any decisions or actions taken by users based on
          information provided on this website. Counseling outcomes may vary depending on individual engagement and other factors.
        </p>

        <h4><strong>Related Policies</strong></h4>
        <p>
          Please also review the following policies available on this site:{" "}
          <Link to="/policies#privacy">Privacy Policy</Link>,{" "}
          <Link to="/policies#refund">Refund and Cancellation Policy</Link>, and <SocialMediaPolicyLink />. Together
          these documents outline your rights, my responsibilities, and the standards I uphold at Healing Hues.
        </p>

        <h4><strong>Modifications</strong></h4>
        <p>
          Healing Hues reserves the right to update or modify these Terms and Conditions at any time. Updated terms will be
          posted on this page with a revised "Last updated" date.
        </p>

        <h4><strong>Governing Law</strong></h4>
        <p>These terms are governed by the laws of The Commonwealth of The Bahamas.</p>

        <h4><strong>Contact</strong></h4>
        <p>
          If you have any questions about these Terms and Conditions, please contact:{" "}
          <Email />.
        </p>
      </>
    ),
  },

  {
    key: "accessibility",
    label: "Accessibility Statement",
    content: (
      <>
        <LastUpdated />
        <p>
          At Healing Hues, I am committed to making my website and virtual counseling and art therapy services accessible
          to all individuals, regardless of ability or background. I strive to ensure that my site and services are inclusive,
          supportive, and usable for everyone.
        </p>

        <h3><strong>My goal</strong></h3>
        <p>
          I aim for this website to meet the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. The site has
          not yet had an independent accessibility audit, so I do not claim full conformance.
        </p>

        <h3><strong>What I have done so far</strong></h3>
        <ul>
          <li>Set the language of the site and structured each page with headings</li>
          <li>Added a "Skip to main content" link and visible focus outlines for keyboard users</li>
          <li>Made the menu, FAQ answers and other expandable sections usable with a keyboard and announced as open or closed to screen readers</li>
          <li>Added text alternatives to informative images, including a text version of my local resources flyer and descriptions of the grounding exercises</li>
          <li>Adjusted button and footer text colours to meet the WCAG AA contrast ratio for normal text</li>
          <li>Turned off decorative animations when your device's "reduce motion" setting is on</li>
          <li>Made emergency phone numbers tappable to call</li>
        </ul>

        <h3><strong>Known limitations</strong></h3>
        <ul>
          <li>Some images contain text. Where they carry information, a text version is provided next to the image.</li>
          <li>I am still reviewing the full site against WCAG 2.1 AA and fixing issues as I find them.</li>
        </ul>

        <h3><strong>Requests, issues, and suggestions</strong></h3>
        <p>
          If you find an accessibility issue on the site, or if you need information in a different format or help to
          book a session, please contact me at <Email />.
        </p>
      </>
    ),
  },
  {
    // Her text from round 2 (answer 3.5, Nov 2026), word for word. Privacy
    // and Terms link here (SOCIAL_MEDIA_POLICY_PATH).
    key: "social-media",
    label: "Social Media and Communication Policy",
    content: (
      <>
        <h3><strong>Purpose</strong></h3>
        <p>
          This policy explains how I use social media and how to communicate with me, so that your privacy and our
          therapeutic relationship stay protected. It applies to Instagram (@healinghues242), the Healing Hues Facebook
          page, email, and messaging.
        </p>

        <h3><strong>Following and Connecting</strong></h3>
        <p>
          You are welcome to follow @healinghues242 and the Healing Hues Facebook page. To protect your confidentiality,
          I do not follow, friend, or accept requests from current or former clients on any personal or professional
          account, and I will not acknowledge clients publicly. Following my pages does not create a therapeutic
          relationship.
        </p>

        <h3><strong>Comments, Likes, and Tags</strong></h3>
        <p>
          Comments, likes, and tags on public pages are visible to others and may reveal that you are connected to
          therapy. Please keep this in mind when engaging. I do not respond to personal or clinical concerns in
          comments.
        </p>

        <h3><strong>Messages and Email</strong></h3>
        <p>
          Direct messages and email are for general questions, booking, and free resources, such as requesting the
          Sunday Studio journal. Please do not share personal mental health details through direct messages, text, or
          email, as these are not fully secure. I am happy to set up a private space for those conversations. I aim to
          respond within two business days.
        </p>

        <h3><strong>Not for Emergencies</strong></h3>
        <p>
          Social media, email, and messages are not monitored around the clock and are not a crisis service. If you are
          in crisis, please call <a href="tel:911">911</a> or <a href="tel:919">919</a>, or see the crisis lines on
          my <Link to="/resources">Resources page</Link>.
        </p>

        <h3><strong>Online Searching</strong></h3>
        <p>
          I do not search for clients online, except in rare situations where safety is a concern. If that ever
          happens, I will discuss it with you.
        </p>

        <h3><strong>Reviews and Testimonials</strong></h3>
        <p>
          I do not ask current clients for reviews or testimonials. If you choose to post a review on your own, I will
          not respond publicly to protect your confidentiality.
        </p>

        <h3><strong>Sharing Artwork</strong></h3>
        <p>
          No client artwork, images, or identifying information is shared without explicit consent. When you
          submit artwork for Sunday Studio or a community feature, I will always ask your permission before sharing it,
          and submitting does not create a therapeutic relationship.
        </p>

        <h3><strong>Questions?</strong></h3>
        <p>
          If you have any questions about this policy, please reach out at <Email />.
        </p>
      </>
    ),
  },
];
